export interface EmployeeOption {
  id: string
  employeeCode: string
  displayName: string
  departmentName: string | null
  designationName: string | null
}

export interface AssignmentView {
  id: string
  employeeId: string
  effectiveFrom: string
  effectiveTo: string | null
  current: boolean
  reason: 'HIRE' | 'TRANSFER' | 'PROMOTION' | 'COST_CENTRE_CHANGE' | 'REVERT' | 'CORRECTION' | 'REHIRE'
  establishmentId: string | null
  establishmentName: string | null
  divisionId: string | null
  divisionName: string | null
  departmentId: string | null
  departmentName: string | null
  designationId: string | null
  designationName: string | null
  gradeId: string | null
  gradeName: string | null
  locationId: string | null
  locationName: string | null
  costCenterId: string | null
  costCenterName: string | null
  positionId: string | null
  managerEmployeeId: string | null
  managerName: string | null
  shiftId: string | null
  workLocation: string | null
  fte: number
  notes: string | null
}

export type TransferType = 'PERMANENT' | 'TEMPORARY'

export type TransferStatus =
  | 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED'
  | 'RETURNED' | 'CANCELLED' | 'APPLIED' | 'REVERTED'

export interface TransferView {
  id: string
  employeeId: string
  employeeName: string | null
  transferType: TransferType
  effectiveFrom: string
  plannedEndDate: string | null
  status: TransferStatus
  toDepartmentId: string | null
  toDepartmentName: string | null
  toLocationId: string | null
  toLocationName: string | null
  toDesignationId: string | null
  toDesignationName: string | null
  toGradeId: string | null
  toCostCenterId: string | null
  toManagerEmployeeId: string | null
  reason: string | null
  decisionComment: string | null
  createdAssignmentId: string | null
  submittedAt: string | null
  decidedAt: string | null
}

/** Why an assignment row exists — shown on the timeline. */
export const ASSIGNMENT_REASONS: Record<AssignmentView['reason'], string> = {
  HIRE: 'Joined',
  TRANSFER: 'Transferred',
  PROMOTION: 'Promoted',
  COST_CENTRE_CHANGE: 'Cost centre changed',
  REVERT: 'Returned from secondment',
  CORRECTION: 'Correction',
  REHIRE: 'Rehired',
}

/** One credential rule the person does not satisfy, as the transfer screen shows it. */
export interface TransferCredentialIssue {
  ruleId: string
  ruleName: string
  mode: string
  documentTypeName: string | null
  status: string
  expiryDate: string | null
  daysRemaining: number | null
  overridden: boolean
}

/**
 * Everything the transfer approval screen needs in one call.
 *
 * `costImpact` is deliberately null with `costImpactReason` saying why: nothing can compute
 * what a grade change costs until the payroll element graph has values, and a figure guessed
 * from a grade band is a number somebody would budget from.
 */
export interface TransferDecisionContext {
  request: TransferView
  currentAssignment: AssignmentView | null
  costCentreChanges: boolean
  fromCostCentre: string | null
  toCostCentre: string | null
  effectiveFrom: string
  daysFromToday: number
  backdated: boolean
  blockingCredentials: TransferCredentialIssue[]
  credentialWarnings: TransferCredentialIssue[]
  costImpact: number | null
  costImpactReason: string | null
}


/** Phase 7 part 5: the directory and the org chart, which had no screens. */
export interface EmployeeSummary {
  id: string
  employeeCode: string
  displayName: string
  workEmail: string | null
  workPhone: string | null
  photoFileId: string | null
  status: string
  employmentType: string
  departmentId: string | null
  departmentName: string | null
  designationId: string | null
  designationName: string | null
  gradeId: string | null
  gradeName: string | null
  locationId: string | null
  locationName: string | null
  managerId: string | null
  managerName: string | null
  joinDate: string | null
}

export interface OrgChartNode {
  id: string
  displayName: string
  employeeCode: string
  designationName: string | null
  departmentName: string | null
  photoFileId: string | null
  directReportCount: number
  reports: OrgChartNode[]
}

/** The profile behind /employees/:id. */
export interface EmployeeDetail {
  summary: EmployeeSummary
  /** The name in parts, so an edit form can put it back in its fields without guessing. */
  firstName: string
  middleName: string | null
  lastName: string | null
  personId: string
  legalEntityId: string | null
  establishmentId: string | null
  establishmentName: string | null
  currentAssignmentId: string | null
  gender: string | null
  dateOfBirth: string | null
  nationality: string | null
  maritalStatus: string | null
  personalEmail: string | null
  personalPhone: string | null
  probationEndDate: string | null
  confirmationDate: string | null
  exitDate: string | null
  noticePeriodDays: number | null
  rehireEligible: boolean | null
  contractType: string | null
  contractEndDate: string | null
  positionId: string | null
  costCenterId: string | null
  divisionId: string | null
}

/** One entry on the career timeline. */
export interface TimelineEntry {
  id: string
  eventType: string
  title: string
  detail: string | null
  eventDate: string
  icon: string | null
}

// --- CRUD completion, group 1: the writes the profile never had ---

/**
 * A bank account as the profile shows it.
 *
 * The account number arrives masked and never leaves the server whole. Whoever is looking at
 * a profile is checking that the account on file is the right one, and the last four digits
 * answer that; the full number answers a question nobody on this screen is asking, and puts
 * it in a screenshot and a browser cache.
 */
export interface BankAccountView {
  id: string
  bankName: string
  branch: string | null
  accountHolderName: string | null
  accountNumberMasked: string | null
  iban: string | null
  routingCode: string | null
  swiftCode: string | null
  currency: string | null
  primaryFlag: boolean
}
